import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    crudStatusEnum,
    getPriceTypeLabel,
    getTruckGroupLabel,
    systemConfigKeyEnum,
    truckGroupEnum,
    truckPriceTypeEnum,
} from '../../../../constants';
import { useCreateTruckTypeMutation, useUpdateTruckTypeMutation } from '../../../../hooks/mutations/useTruckTypeMutation';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { TruckType } from '../../../../services/apiSLTP/materials/trucksTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    truckType: TruckType | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateTruckTypeModal: React.FC<ModalProps> = ({ truckType, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [designation, setDesignation] = useState(truckType?.designation || '');
    const [status, setStatus] = useState(truckType?.crudStatus || crudStatusEnum.OK);
    const [isDumpAllowed, setIsDumpAllowed] = useState(truckType?.isDumpAllowed || false);
    const [defaultTonnage, setDefaultTonnage] = useState(truckType?.defaultTonnage?.toString() || '');
    const [minTonnage, setMinTonnage] = useState(truckType?.minTonnage?.toString() || '');
    const [maxTonnage, setMaxTonnage] = useState(truckType?.maxTonnage?.toString() || '');

    const [truckGroup, setTruckGroup] = useState({
        label: capitalizeFirstLetter(t(getTruckGroupLabel(truckType?.group || truckGroupEnum.TRUCK_8X4))),
        value: (truckType?.group || truckGroupEnum.TRUCK_8X4).toString(),
    });
    const [price, setPrice] = useState(typeof truckType?.price === 'number' ? truckType?.price.toString() : '');
    const [priceType, setPriceType] = useState({
        label: capitalizeFirstLetter(t(getPriceTypeLabel(truckType?.priceType || truckPriceTypeEnum.DAY))),
        value: (truckType?.priceType || truckPriceTypeEnum.DAY).toString(),
    });
    const [displayOrder, setDisplayOrder] = useState(typeof truckType?.displayOrder === 'number' ? truckType?.displayOrder.toString() : '');

    const { mutate: mutateTruckType } = useUpdateTruckTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating truckType:', error.message),
    });

    const { mutate: addTruckType } = useCreateTruckTypeMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding truckType:', error.message),
    });

    const handleSubmit = () => {
        if (isEditMode) {
            if (truckType?._id)
                mutateTruckType({
                    truckTypeId: truckType?._id,
                    data: {
                        designation,
                        crudStatus: status,
                        priceType: parseInt(priceType.value),
                        price: parseFloat(price),
                        group: parseInt(truckGroup.value),
                        defaultTonnage: !isNaN(parseFloat(defaultTonnage)) ? parseFloat(defaultTonnage) : null,
                        minTonnage: !isNaN(parseFloat(minTonnage)) ? parseFloat(minTonnage) : null,
                        maxTonnage: maxTonnage.length ? parseFloat(maxTonnage) : null,
                        isDumpAllowed,
                        displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                    },
                });
        } else {
            addTruckType({
                designation,
                crudStatus: status,
                priceType: parseInt(priceType.value),
                price: parseFloat(price),
                group: parseInt(truckGroup.value),
                defaultTonnage: !isNaN(parseFloat(defaultTonnage)) ? parseFloat(defaultTonnage) : null,
                minTonnage: !isNaN(parseFloat(minTonnage)) ? parseFloat(minTonnage) : null,
                maxTonnage: !isNaN(parseFloat(maxTonnage)) ? parseFloat(maxTonnage) : null,
                isDumpAllowed,
                displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
            });
        }
    };

    return (
        <ModalBody
            title={t('truckType')}
            subtitle={isEditMode ? t('update') : t('add')}
            disableSubmit={!(designation.length && price.length && priceType.value.length && truckGroup.value.length)}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={(text) => setDesignation(text)} required />
            <ToggleLine
                checked={status === crudStatusEnum.OK}
                onChange={(checked) => setStatus(checked ? crudStatusEnum.OK : crudStatusEnum.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />
            <DropDown
                selectedItem={truckGroup}
                handleChange={(event, value) => {
                    value && setTruckGroup(value);
                }}
                inputLabel={t('group')}
                items={Object.values(truckGroupEnum).map((group) => ({
                    label: capitalizeFirstLetter(t(getTruckGroupLabel(group))),
                    value: group.toString(),
                }))}
                required
            />
            <ToggleLine checked={isDumpAllowed} onChange={(checked) => setIsDumpAllowed(checked)} title={capitalizeFirstLetter(t('dumpAllowed'))} />
            <CustomTextInput value={defaultTonnage} onChange={(text) => setDefaultTonnage(text)} label={t('defaultTonnage')} type="number" />
            <CustomTextInput
                value={minTonnage}
                onChange={(text) => setMinTonnage(text)}
                label={t('minTonnage')}
                type="number"
                error={!!maxTonnage.length && !!minTonnage.length && (parseFloat(minTonnage) < 0 || parseFloat(maxTonnage) < parseFloat(minTonnage))}
            />
            <CustomTextInput
                value={maxTonnage}
                onChange={(text) => setMaxTonnage(text)}
                label={t('maxTonnage')}
                type="number"
                error={!!maxTonnage.length && !!minTonnage.length && (parseFloat(maxTonnage) < 0 || parseFloat(maxTonnage) < parseFloat(minTonnage))}
            />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                required
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(truckPriceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    if (value) setPriceType(value);
                }}
                inputLabel={t('pricing')}
                required
            />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateTruckTypeModal;
